import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Mail, Loader2, ArrowLeft, Check } from 'lucide-react'
import { resetPassword } from '../../utils/auth'

export default function ForgotPassword() {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState('idle') // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('')

    const handleSubmit = async (e) => {
        e.preventDefault()
        setErrorMessage('')

        if (!email) {
            setStatus('error')
            setErrorMessage('Please enter your email address')
            return
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setStatus('error')
            setErrorMessage('Please enter a valid email address')
            return
        }

        setStatus('loading')

        try {
            const { error } = await resetPassword(email)

            if (error) {
                throw new Error(error.message)
            }

            setStatus('success')
        } catch (error) {
            setStatus('error')
            setErrorMessage(error.message || 'Failed to send reset email')
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 animated-gradient-bg">
            {/* Background Effects */}
            <div className="blob w-72 h-72 bg-primary-500/20 -top-20 -left-20" />
            <div className="blob w-96 h-96 bg-accent-500/20 -bottom-20 -right-20" />

            <div className="relative z-10 w-full max-w-md">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-2 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-white">InkSpot</span>
                </Link>

                {/* Card */}
                <div className="glass-card p-8">
                    {status === 'success' ? (
                        // Success State
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                <Check className="w-8 h-8 text-green-400" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2">
                                Check your email
                            </h1>
                            <p className="text-white/60 mb-6">
                                We've sent a password reset link to {email}
                            </p>
                            <Link to="/signin" className="btn-secondary w-full">
                                <ArrowLeft className="w-5 h-5 mr-2" />
                                Back to sign in
                            </Link>
                        </div>
                    ) : (
                        // Form State
                        <>
                            <h1 className="text-2xl font-bold text-white text-center mb-2">
                                Reset your password
                            </h1>
                            <p className="text-white/60 text-center mb-8">
                                Enter your email and we'll send you a reset link
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* Email */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                                        Email address
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="input-field pl-11"
                                            placeholder="you@example.com"
                                        />
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                    </div>
                                </div>

                                {/* Error Message */}
                                {status === 'error' && (
                                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                                        <p className="text-sm text-red-400">{errorMessage}</p>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className="btn-primary w-full disabled:opacity-50"
                                >
                                    {status === 'loading' ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Send reset link'
                                    )}
                                </button>
                            </form>

                            {/* Back Link */}
                            <div className="mt-6 text-center">
                                <Link to="/signin" className="text-primary-400 hover:text-primary-300 text-sm inline-flex items-center">
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    Back to sign in
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
